import { useContext, useEffect, useMemo, useState } from 'react';

import { patchEquipment } from '@services/queries/equipments';
import { useMutation, useQueryClient } from 'react-query';
import { Prompt, useParams } from 'react-router-dom';

import { isSameDay } from 'date-fns';
import { getPermissionData } from '@utils';
import { NavigationContext } from '@layout/navigation/navigation';
import ReactTooltip from 'react-tooltip';
import { ITooltip } from '@components/Tooltip';
import { setAttr } from '@utils/setAttr';
import { AssetContext } from '@pages/Asset/Asset.query';
import { Buttons, Cancel, Done } from './EditableDateField.styles';
import { DateInputAssets } from '../DateInputAssets/DateInputAssets';

interface InfoEditableProps {
  content: Date | null;
  parameter: string;
  pinnedData?: boolean;
}
const EditableDateField = ({
  content,
  parameter,
  pinnedData,
}: InfoEditableProps) => {
  const params = useParams<{ equipmentId: string }>();

  const { role } = useContext(NavigationContext);
  const { isEditingField, setIsEditingField } = useContext(AssetContext);

  const [initialValue, setInitialValue] = useState(content);
  const [label, setLabel] = useState(content);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (isEditingField !== parameter) {
      setInitialValue(content);
      setLabel(content);
    }
  }, [content]);

  const mutationPatchEquipment = useMutation(patchEquipment, {
    onSuccess: () => {
      queryClient.invalidateQueries(['equipments', params.equipmentId]);
      setIsEditingField('');
      setIsDirty(false);
    },
  });

  const cancel = () => {
    setLabel(initialValue);
    setIsEditingField('');
    setIsDirty(false);
  };

  const editPermission = getPermissionData({
    action: 'edit',
    owner: true,
    role,
    type: 'Asset Data',
  });
  const { allowed, message } = editPermission;

  const tooltipText = allowed ? '' : message;
  const handleClickIn = () => {
    if (allowed) {
      setIsEditingField(parameter);
    }
    // No need to stop propagation as DateInputs have a disabled state
  };

  const submit = () => {
    const response = {
      [parameter]: label,
    } as any;

    mutationPatchEquipment.mutate({
      id: params.equipmentId,
      data: response,
    });
  };

  const handleOnChange = (event: any) => {
    if (event === null) setLabel(event);
    const parsedDate = Date.parse(event);
    if (parsedDate) {
      setLabel(new Date(parsedDate));
      setIsDirty(true);
    }
  };

  const submitDisabled = useMemo(() => {
    if (mutationPatchEquipment.isLoading) {
      return true;
    }

    if (label) {
      return isSameDay(label as Date, initialValue as Date);
    }

    return label === initialValue;
  }, [label, initialValue, mutationPatchEquipment.isLoading]);

  return (
    <div
      data-tip={tooltipText}
      data-for={`editAssetData-${parameter}`}
      // This is For date inputs, popover doesn't work when data-event is 'click'
      data-event={allowed ? undefined : 'click'}
    >
      <DateInputAssets
        handleDateChange={handleOnChange}
        value={label}
        divProps={{
          onClick: handleClickIn,
          'data-active': isEditingField === parameter,
        }}
        pinnedData={pinnedData}
        direction={pinnedData ? 'rtl' : ''}
        disabled={!allowed}
      />

      <ITooltip
        id={`editAssetData-${parameter}`}
        globalEventOff="click"
        disable={setAttr(allowed)}
        afterShow={() => {
          setTimeout(ReactTooltip.hide, 1000);
        }}
      />
      {isEditingField === parameter && (
        <Buttons>
          <Cancel onClick={cancel} type="button">
            Cancel
          </Cancel>
          <Done onClick={submit} type="button" disabled={submitDisabled}>
            Done
          </Done>
        </Buttons>
      )}

      <Prompt
        when={isDirty}
        message="You have unsaved changes, Are you sure you want to leave?"
      />
    </div>
  );
};

export { EditableDateField };
